import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Switch,
  Table,
  Typography,
} from "antd";
import type { WbSlotConfig, WbSlotOffer } from "../api/WbSlots";
import { useWbSlots } from "./useWbSlots";

const CommitNumber = ({
  value,
  onDraft,
  onCommit,
  ...rest
}: {
  value: number | null | undefined;
  onDraft: (value: number | null) => void;
  onCommit: (value: number | null) => void;
} & Omit<
  React.ComponentProps<typeof InputNumber>,
  "value" | "onChange" | "onBlur"
>) => {
  const latest = useRef(value);
  const touched = useRef(false);
  latest.current = value;
  return (
    <InputNumber
      {...rest}
      value={value ?? undefined}
      onChange={(next) => {
        touched.current = true;
        const num = next == null || next === "" ? null : Number(next);
        const value = num == null || Number.isNaN(num) ? null : num;
        latest.current = value;
        onDraft(value);
      }}
      onBlur={() => {
        if (!touched.current) return;
        onCommit(latest.current ?? null);
      }}
    />
  );
};

const WbSlots = () => {
  const {
    config,
    demand,
    health,
    offers,
    offersUpdatedAt,
    bookings,
    blocks,
    supplies,
    suppliesLoading,
    analytics,
    patchLocal,
    save,
    excludeWarehouse,
    restoreWarehouse,
    refreshLive,
    refreshSupplies,
    liveLoading,
    demandLoading,
    blocksLoading,
    bookingsLoading,
    analyticsLoading,
    loadDemand,
    loadBlocks,
    loadBookings,
    loadAnalytics,
  } = useWbSlots();
  const [blockWarehouseId, setBlockWarehouseId] = useState<number | null>(null);
  const barcodeTouched = useRef(false);

  const demandColumns = useMemo(
    () => [
      { title: "Дата", dataIndex: "date" },
      { title: "От сегодня", dataIndex: "offset" },
      { title: "Нужно", dataIndex: "needed" },
      { title: "Забронировано", dataIndex: "booked" },
      { title: "Осталось", dataIndex: "remaining" },
    ],
    []
  );

  const offersColumns = useMemo(
    () => [
      { title: "ID", dataIndex: "warehouseId" },
      { title: "Склад", dataIndex: "name" },
      { title: "Класс", dataIndex: "class" },
      {
        title: "",
        render: (_: unknown, row: WbSlotOffer) => (
          <Button
            size="small"
            onClick={() => excludeWarehouse(row.warehouseId)}
          >
            Исключить
          </Button>
        ),
      },
    ],
    [excludeWarehouse]
  );

  const blocksColumns = useMemo(
    () => [
      { title: "Склад", dataIndex: "warehouse_id" },
      { title: "До", dataIndex: "blocked_until" },
      { title: "Причина", dataIndex: "reason" },
      { title: "Код", dataIndex: "error_code" },
      { title: "Источник", dataIndex: "source" },
      {
        title: "",
        render: (_: unknown, row: any) => (
          <Button size="small" onClick={() => restoreWarehouse(row.id)}>
            Вернуть
          </Button>
        ),
      },
    ],
    [restoreWarehouse]
  );

  const bookingsColumns = useMemo(
    () => [
      { title: "Дата", dataIndex: "target_date" },
      { title: "Склад", dataIndex: "warehouse_name" },
      { title: "Статус", dataIndex: "status" },
      { title: "Ошибка", dataIndex: "error_code" },
    ],
    []
  );

  const suppliesColumns = useMemo(
    () => [
      { title: "ID", dataIndex: "supplyId" },
      { title: "Склад", dataIndex: "warehouseName" },
      { title: "Дата", dataIndex: "supplyDate" },
      { title: "Статус", dataIndex: "statusName" },
    ],
    []
  );

  if (!config) return <div>Загрузка…</div>;

  const commit =
    (key: keyof WbSlotConfig) => (value: number | string | null) =>
      save({ [key]: value } as Partial<WbSlotConfig>);

  return (
    <>
      <Typography.Title level={4}>Охота за слотами WB</Typography.Title>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <span style={{ marginRight: 8 }}>Поиск</span>
          <Switch
            checked={config.enabled}
            onChange={(enabled) => save({ enabled })}
          />
        </Col>
        <Col>
          <span style={{ marginRight: 8 }}>Автобронь</span>
          <Switch
            checked={config.booking_enabled}
            onChange={(booking_enabled) => save({ booking_enabled })}
          />
        </Col>
        <Col>
          Статус:{" "}
          {health == null
            ? "—"
            : health.alive
              ? `жив · ${health.session?.state || "—"}`
              : "охота не отвечает"}
          {offersUpdatedAt && (
            <span style={{ marginLeft: 12, color: "#888" }}>
              карта: {new Date(offersUpdatedAt).toLocaleTimeString()}
            </span>
          )}
          <Button
            size="small"
            loading={liveLoading}
            onClick={refreshLive}
            style={{ marginLeft: 12 }}
          >
            Обновить статус и карту
          </Button>
        </Col>
      </Row>

      <Typography.Title level={5}>
        Окно спроса{" "}
        <Button size="small" loading={demandLoading} onClick={loadDemand}>
          Обновить
        </Button>
      </Typography.Title>
      <Form layout="inline" style={{ marginBottom: 12 }}>
        <Form.Item label="Не раньше, дней">
          <CommitNumber
            min={0}
            value={config.min_day}
            onDraft={(v) => v != null && patchLocal({ min_day: v })}
            onCommit={(v) => v != null && save({ min_day: v })}
          />
        </Form.Item>
        <Form.Item label="Длина окна">
          <CommitNumber
            min={1}
            value={config.horizon_days}
            onDraft={(v) => v != null && patchLocal({ horizon_days: v })}
            onCommit={(v) => v != null && save({ horizon_days: v })}
          />
        </Form.Item>
        <Form.Item label="Складов на день">
          <CommitNumber
            min={1}
            value={config.needed_per_day}
            onDraft={(v) => v != null && patchLocal({ needed_per_day: v })}
            onCommit={(v) => v != null && save({ needed_per_day: v })}
          />
        </Form.Item>
      </Form>
      <Table
        size="small"
        pagination={false}
        rowKey="date"
        dataSource={demand}
        columns={demandColumns}
        style={{ marginBottom: 24 }}
      />

      <Typography.Title level={5}>Область и состав</Typography.Title>
      <Form layout="inline" style={{ marginBottom: 12 }}>
        <Form.Item label="lat top">
          <CommitNumber
            value={config.lat_top}
            onDraft={(v) => patchLocal({ lat_top: v })}
            onCommit={commit("lat_top")}
          />
        </Form.Item>
        <Form.Item label="lon top">
          <CommitNumber
            value={config.lon_top}
            onDraft={(v) => patchLocal({ lon_top: v })}
            onCommit={commit("lon_top")}
          />
        </Form.Item>
        <Form.Item label="lat end">
          <CommitNumber
            value={config.lat_end}
            onDraft={(v) => patchLocal({ lat_end: v })}
            onCommit={commit("lat_end")}
          />
        </Form.Item>
        <Form.Item label="lon end">
          <CommitNumber
            value={config.lon_end}
            onDraft={(v) => patchLocal({ lon_end: v })}
            onCommit={commit("lon_end")}
          />
        </Form.Item>
        <Form.Item label="Штрихкод">
          <Input
            value={config.barcode}
            onChange={(e) => {
              barcodeTouched.current = true;
              patchLocal({ barcode: e.target.value });
            }}
            onBlur={() => {
              if (!barcodeTouched.current) return;
              save({ barcode: config.barcode });
            }}
          />
        </Form.Item>
        <Form.Item label="Кол-во">
          <CommitNumber
            value={config.products_count}
            onDraft={(v) => v != null && patchLocal({ products_count: v })}
            onCommit={(v) => v != null && save({ products_count: v })}
          />
        </Form.Item>
        <Form.Item label="Паллет">
          <CommitNumber
            value={config.pallet_count}
            onDraft={(v) => patchLocal({ pallet_count: v })}
            onCommit={commit("pallet_count")}
          />
        </Form.Item>
      </Form>

      <Typography.Title level={5}>Склады с последней карты</Typography.Title>
      {!offers.length && (
        <p style={{ color: "#888", marginBottom: 8 }}>
          Нажмите «Обновить статус и карту» выше
        </p>
      )}
      <Table
        size="small"
        pagination={{ pageSize: 20 }}
        rowKey="warehouseId"
        dataSource={offers}
        columns={offersColumns}
        style={{ marginBottom: 24 }}
      />

      <Typography.Title level={5}>
        Исключения{" "}
        <Button size="small" loading={blocksLoading} onClick={loadBlocks}>
          Обновить
        </Button>
      </Typography.Title>
      <Form layout="inline" style={{ marginBottom: 8 }}>
        <Form.Item label="ID склада">
          <InputNumber
            value={blockWarehouseId ?? undefined}
            onChange={(v) => setBlockWarehouseId(v)}
          />
        </Form.Item>
        <Button
          onClick={() => {
            if (!blockWarehouseId) return;
            excludeWarehouse(blockWarehouseId).then(() =>
              setBlockWarehouseId(null)
            );
          }}
        >
          Добавить
        </Button>
      </Form>
      <Table
        size="small"
        pagination={false}
        rowKey="id"
        dataSource={blocks}
        columns={blocksColumns}
        style={{ marginBottom: 24 }}
      />

      <Typography.Title level={5}>
        Брони бота{" "}
        <Button size="small" loading={bookingsLoading} onClick={loadBookings}>
          Обновить
        </Button>
      </Typography.Title>
      <Table
        size="small"
        pagination={{ pageSize: 10 }}
        rowKey="id"
        dataSource={bookings}
        columns={bookingsColumns}
        style={{ marginBottom: 24 }}
      />

      <Typography.Title level={5}>
        Активные поставки WB{" "}
        <Button
          size="small"
          loading={suppliesLoading}
          onClick={refreshSupplies}
        >
          Обновить поставки
        </Button>
      </Typography.Title>
      <Table
        size="small"
        pagination={{ pageSize: 10 }}
        rowKey={(row) => String(row.supplyId || row.preorderId)}
        dataSource={supplies}
        columns={suppliesColumns}
        style={{ marginBottom: 24 }}
      />

      <Typography.Title level={5}>
        Аналитика{" "}
        <Button size="small" loading={analyticsLoading} onClick={loadAnalytics}>
          Обновить
        </Button>
      </Typography.Title>
      {analytics ? (
        <p>
          Забронировано: {analytics.booked}. Ошибок: {analytics.failed}.
        </p>
      ) : (
        <p style={{ color: "#888" }}>Нет данных</p>
      )}
    </>
  );
};

export default WbSlots;
